import React from "react";
import {NonLifestyleGoalType} from "../models/GoalType";
import {GoalModelType} from "../models/GoalModelType";
import {selectGoalModel} from "../controller/GoalsModelSlice";
import {useAppSelector} from "../../store/hooks";
import DataEntrySummary, {DataEntrySummaryItem} from "../../components/DataEntry/DataEntrySummary";
import {InfoPopover} from "../../components";
import {formatCurrency, formatNumber} from "../../utils/format";
import GoalUtils from "../Summary/GoalUtils";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";
import {DiscountRateType} from "../models/DiscountRate";


const NonLifestyleGoalLivePreview = ({nonLifestyleGoal}: { nonLifestyleGoal: NonLifestyleGoalType }) => {
    const {investorGroup, discountRateSelection} = useAppSelector<GoalModelType>(selectGoalModel)
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const enableEnhancedLifestyleFunding = releaseToggles?.enableEnhancedLifestyleFunding ?? false;

    const {yearsUntilFlow, yearsOfFlow} = GoalUtils.getYearsOfFlowAndYearsUntilFlow(nonLifestyleGoal, investorGroup)
    const {calculatedFields} = nonLifestyleGoal;
    const isFundedByNonInvestableAssets = nonLifestyleGoal.userInputs.isFundedByNonInvestableAssets;

    const baseItems: DataEntrySummaryItem[] = [
        {
            label: 'Annual Flow',
            value: formatCurrency(Number(nonLifestyleGoal.userInputs.annualFlow))
        },
        {
            label: 'Years until Flow',
            value: `${yearsUntilFlow} years`
        },
        {
            label: 'Years of Flow',
            value: `${yearsOfFlow} years`
        },
        {
            label: 'Present Value',
            value: formatCurrency(calculatedFields.presentValue)
        }
    ];

    const enhancedItems: DataEntrySummaryItem[] = enableEnhancedLifestyleFunding ? [
        {
            label: 'Funded by Inflows',
            value: calculatedFields.presentValueFundedByInflows != 0 ? `-${formatCurrency(calculatedFields.presentValueFundedByInflows)}` : "$0"
        },
        {
            label: 'Funded by Savings',
            value: calculatedFields.presentValueFundedBySavings != 0 ? `-${formatCurrency(calculatedFields.presentValueFundedBySavings)}` : "$0"
        },
        ...(isFundedByNonInvestableAssets ? [{
            label: 'Funded by Non-Investable',
            value: formatCurrency(calculatedFields.presentValueFundedByNonInvestables)
        }] : []),
        {
            label: 'Funded by Portfolio',
            value: formatCurrency(calculatedFields.presentValueFundedByPortfolio)
        },
        {
            label: (
                <div style={{minHeight: "24px"}}>Risk Assets / Risk Control
                    <InfoPopover
                        content={
                            "This allocation only reflects the portion of the goal that requires portfolio spending"
                        }
                        direction="left"
                        width="220px"
                        arrow
                    />
                </div>
            ),
            value: calculatedFields.requiredPortfolioRiskAsset == null || calculatedFields.requiredPortfolioRiskControl == null
                ? "--"
                : `${Math.round(calculatedFields.requiredPortfolioRiskAsset)}% / ${Math.round(calculatedFields.requiredPortfolioRiskControl)}%`
        },
        {
            label: 'Investable Shortfall',
            value: calculatedFields.investableShortfall === 0 ? "--" : formatCurrency(calculatedFields.investableShortfall)
        },
        discountRateSelection === DiscountRateType.TRIPLE_NET
            ? {
                label: 'Triple Net Discount Rate',
                value: `${formatNumber(calculatedFields!.tripleNetDiscountRate)}%`
            }
            : {
                label: 'Gross Discount Rate',
                value: `${formatNumber(calculatedFields!.grossDiscountRate)}%`
            }
    ] : [];

    return <div className="non-lifestyle-goal-live-preview">
        <section>
            <DataEntrySummary title="Goal Summary"
                              items={[...baseItems, ...enhancedItems]}/>
        </section>
    </div>
}

export default NonLifestyleGoalLivePreview;
